import TranscriptionFactReference from './TranscriptionFactReference';

interface TextWithFactReferencesProps {
  text: string;
  caseId: string;
  onFactClick?: (factId: string) => void;
  className?: string;
}

// Regex to match references in formats:
// [1]{fact-id} - single reference
// [2, 3]{fact-id1,fact-id2} - multiple references in brackets
const REFERENCE_REGEX = /\[([0-9, ]+)\]\{([a-f0-9,-]+)\}/g;

// TODO: Unify all referencing logic across the app (transcription, Kim, etc) in the future

const TextWithFactReferences = ({ text, caseId, onFactClick, className = '' }: TextWithFactReferencesProps) => {
  if (!text) {
    return null;
  }

  const parts: (string | { type: 'reference'; number: number; factId: string })[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex lastIndex to ensure we start from the beginning
  REFERENCE_REGEX.lastIndex = 0;

  while ((match = REFERENCE_REGEX.exec(text)) !== null) {
    const [fullMatch, referenceNumbers, factIds] = match;
    const matchStart = match.index;

    // Add text before the match
    if (matchStart > lastIndex) {
      parts.push(text.slice(lastIndex, matchStart));
    }

    // Parse multiple references and fact IDs
    const numbers = referenceNumbers
      .split(',')
      .map((n) => n.trim())
      .map((n) => parseInt(n, 10));
    const ids = factIds.split(',').map((id) => id.trim());

    // Create references for each number/ID pair
    numbers.forEach((number, index) => {
      const factId = ids[index] ?? ids[0];
      parts.push({
        type: 'reference',
        number: number,
        factId: factId,
      });

      if (index < numbers.length - 1) {
        parts.push(', ');
      }
    });

    lastIndex = matchStart + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  const hasReferences = parts.some((part) => typeof part === 'object' && part.type === 'reference');

  if (parts.length === 0) {
    return (
      <span className={className} style={{ display: 'inline' }}>
        {text}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline',
        lineHeight: hasReferences ? '1.6' : 'inherit',
      }}
    >
      {parts.map((part, index) => {
        if (typeof part === 'string') {
          return <span key={index}>{part}</span>;
        } else {
          return (
            <TranscriptionFactReference
              key={index}
              factId={part.factId}
              referenceNumber={part.number}
              caseId={caseId}
              onFactClick={onFactClick}
            />
          );
        }
      })}
    </span>
  );
};

export default TextWithFactReferences;
